import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useDeletePlacement, useNodes, usePlacements } from "../../api/queries";
import type { Placement } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { QueryStates } from "../../components/QueryStates";
import { PlacementStatusPill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { useEnvironmentsById } from "../environments/useEnvironmentsById";
import { DeletePlacementDialog } from "./DeletePlacementDialog";
import { EditPlacementModal } from "./EditPlacementModal";
import { PlacePlacementModal } from "./PlacePlacementModal";
import "./services.css";

interface PlacementsSectionProps {
  projectId: string;
  serviceId: string;
  serviceName: string;
  canManage: boolean;
}

/**
 * A service's placements — "this service on this machine" (ADR-0025 §2),
 * user-facing as "Where it runs" (UX polish wave 2 decision 1). This is the
 * placement-centric replacement for the old single desired-count field:
 * where a service runs is now visible and editable per machine, not implied
 * by a replica count.
 */
export function PlacementsSection({ projectId, serviceId, serviceName, canManage }: PlacementsSectionProps) {
  const { pushToast } = useToast();
  const {
    data: placementsPage,
    isPending,
    isError,
    error,
    refetch,
  } = usePlacements(projectId, serviceId);
  // Bounded picker fetch (matches ProvisionInstanceModal's precedent for a
  // small reference list feeding a select/lookup, not a paginated view of
  // its own) — used both to resolve a placement's machine name here and to
  // build the "Add a machine" picker.
  const { data: nodesPage } = useNodes({ limit: 100 });
  // Resolves a placement's node to its environment CLASS (Host/Deploy UX
  // Phase 4, #526) — a node already carries `environmentId`/`environmentName`
  // (see types.ts), but only `Environment` itself carries `class`, the thing
  // that drives the badge's tone.
  const environmentsById = useEnvironmentsById();
  const deleteMutation = useDeletePlacement(projectId, serviceId);

  const [placeOpen, setPlaceOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Placement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Placement | null>(null);

  const nodesById = useMemo(() => {
    const map = new Map(nodesPage?.items.map((n) => [n.id, n]));
    return map;
  }, [nodesPage]);

  // Whether "Add a machine" would actually have somewhere to offer. Undefined
  // (still loading) reads as "assume yes" so the empty state doesn't flash the
  // no-nodes guidance before useNodes resolves — mirrors PlacePlacementModal's
  // own `!nodesPending` gate on the same check.
  const hasEligibleNodes = nodesPage === undefined || nodesPage.items.some((n) => n.status === "online");

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id: placementId, nodeId } = deleteTarget;
    const nodeName = nodesById.get(nodeId)?.name ?? nodeId;
    deleteMutation.mutate(
      { placementId, nodeId },
      {
        onSuccess: () => {
          pushToast({ title: `Stopped running on "${nodeName}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't stop it running on "${nodeName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const placeButton = (
    <Button variant="primary" onClick={() => setPlaceOpen(true)}>
      Add a machine
    </Button>
  );

  // Which env a placement runs in — "staging vs. production at a glance"
  // (Host/Deploy UX Phase 4, #526). A placement carries no environment of its
  // own (ADR-0025 §2's join is service<->node only); it inherits its node's,
  // so a node not attached to one (a BYO host, or a `local` dev machine)
  // renders honestly as "no environment" rather than a fabricated one (A-10).
  function environmentCell(p: Placement) {
    const node = nodesById.get(p.nodeId);
    if (!node?.environmentName) return "—";
    return <EnvironmentBadge name={node.environmentName} envClass={environmentsById.get(node.environmentId)?.class} />;
  }

  const columns: DataTableColumn<Placement>[] = [
    {
      key: "node",
      header: "Machine",
      cell: (p) => nodesById.get(p.nodeId)?.name ?? p.nodeId,
      className: "data-table__primary",
    },
    {
      key: "environment",
      header: "Environment",
      cell: environmentCell,
      className: (p) => (nodesById.get(p.nodeId)?.environmentName ? undefined : "data-table__empty-cell"),
    },
    { key: "status", header: "Status", cell: (p) => <PlacementStatusPill status={p.status} /> },
    {
      key: "coLocate",
      header: "Co-locate",
      cell: (p) => p.coLocate || "—",
      className: (p) => (p.coLocate ? "data-table__secondary" : "data-table__empty-cell"),
    },
    {
      key: "envOverrides",
      header: "Env overrides",
      cell: (p) => Object.keys(p.envOverrides).length,
      className: "data-table__mono",
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (p: Placement) => (
              <div className="services-table__actions">
                <Button variant="secondary" onClick={() => setEditTarget(p)}>
                  Edit
                </Button>
                <Button variant="danger" onClick={() => setDeleteTarget(p)}>
                  Remove
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <section className="service-detail__section" aria-labelledby="service-placements-heading">
      <div className="service-detail__section-header">
        <h2 id="service-placements-heading">Where it runs</h2>
        {canManage && (placementsPage?.items.length ?? 0) > 0 && placeButton}
      </div>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading where it runs…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load where it runs. Check your connection and try again."
        }
        isEmpty={Boolean(placementsPage && placementsPage.items.length === 0)}
        empty={
          <EmptyState
            title="Not running anywhere yet"
            description={
              hasEligibleNodes
                ? "Add a machine to deploy it — a service with nowhere to run can't be deployed."
                : "Add a machine to deploy it — but this fleet has no online machines yet. Give it somewhere to run first."
            }
            action={
              canManage ? (
                hasEligibleNodes ? (
                  placeButton
                ) : (
                  <Link href="/hosts" className="button button--primary">
                    Go to machines
                  </Link>
                )
              ) : undefined
            }
          />
        }
      >
        {placementsPage && (
          <DataTable columns={columns} rows={placementsPage.items} rowKey={(p) => p.id} />
        )}
      </QueryStates>

      {canManage && (
        <PlacePlacementModal
          open={placeOpen}
          projectId={projectId}
          serviceId={serviceId}
          serviceName={serviceName}
          existingPlacements={placementsPage?.items ?? []}
          onClose={() => setPlaceOpen(false)}
        />
      )}

      {canManage && (
        <EditPlacementModal
          placement={editTarget}
          node={editTarget ? nodesById.get(editTarget.nodeId) : undefined}
          projectId={projectId}
          serviceId={serviceId}
          onClose={() => setEditTarget(null)}
        />
      )}

      {canManage && (
        <DeletePlacementDialog
          placement={deleteTarget}
          node={deleteTarget ? nodesById.get(deleteTarget.nodeId) : undefined}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </section>
  );
}
